import { Link, useNavigate, useParams } from "react-router";
import { ProgressBar } from "@/components/ui/ProgressBar";
import { Panel } from "@/components/ui/Panel";
import { toast } from "sonner";
import { Button, buttonChrome } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useFigureReview } from "@/hooks/use-figure-review";
import { useFigures } from "@/hooks/use-figures";
import { FigureLayoutPreview } from "./FigureLayoutPreview";
import { FigureSummaries } from "./FigureSummaries";
import { StagePage } from "../layout/StagePage";
import {
  destinationAfter,
  stageAfter,
  stageHeadingLabel,
} from "@/v2/stage-vocabulary";

/**
 * The last stop in the Figure Engine: check each figure, confirm it, settle the
 * page layout, and insert the set into the draft.
 *
 * The insert gate is the server's rule; the button only reflects the projected
 * `insert_allowed`, so a stale screen cannot insert past a lapsed confirmation.
 */

/**
 * How much of the set is described, while any of it is not.
 *
 * Advisory by design: an unfinished figure invites the attorney back to Define
 * but never blocks the review, because they may have good reason to proceed.
 */
function ReadinessReadout({
  taskId,
  described,
  total,
}: {
  taskId: string;
  described: number;
  total: number;
}) {
  if (total === 0 || described === total) return null;
  return (
    <Panel
      tone="warning"
      data-testid="review-readiness"
      className="mt-4 flex items-center gap-4"
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold">
          {described} of {total} figures described
        </p>
        <p className="text-xs text-muted-foreground">
          Some figures still need a description you have accepted, or component
          titles. The summaries below will show the gaps.
        </p>
      </div>
      <ProgressBar
        value={described}
        max={total}
        tone="warning"
        label="Figures with an accepted description"
        className="w-32 flex-none"
      />
      <Link
        to={`/drafts/${taskId}/figures/define`}
        className={cn(
          buttonChrome({ variant: "secondary", size: "dense" }),
          "flex-none",
        )}
      >
        Define now →
      </Link>
    </Panel>
  );
}

export function FiguresReviewPane() {
  const { taskId } = useParams<{ taskId: string }>();
  // The route always carries the param; a mount without it has nothing to
  // review, and answering nothing beats links into /drafts/undefined.
  if (taskId === undefined) return null;
  return <ReviewOfTask taskId={taskId} />;
}

function ReviewOfTask({ taskId }: { taskId: string }) {
  const { data } = useFigures(taskId);
  const review = useFigureReview(taskId);
  const navigate = useNavigate();

  const insert = () => {
    review.mutate(
      { op: "insert" },
      {
        onSuccess: (state) => {
          if (!state.inserted) {
            toast.error(
              "The figures were not inserted. Confirm every section above and try again.",
            );
            return;
          }
          toast.success(
            "Figures and drawings inserted into the draft. You can still edit them at any time.",
          );
          const next = destinationAfter(taskId, "figures");
          if (next !== undefined) void navigate(next);
        },
      },
    );
  };

  const figures = data?.figures ?? [];
  const described = figures.filter(
    (f) => f.define_status === "complete",
  ).length;
  const insertAllowed = data?.review?.insert_allowed === true;

  return (
    <StagePage
      guide="figures-review"
      testId="figures-review-pane"
      width="wide"
      eyebrow={`${stageHeadingLabel("figures")} · Review Figures`}
      title="Review the figures"
      lede="Check everything captured for each figure, then confirm each section below before inserting. The Detailed Description of Drawings prose is written later, at the draft build."
    >
      <ReadinessReadout
        taskId={taskId}
        described={described}
        total={figures.length}
      />

      {data !== undefined && (
        <>
          <FigureSummaries taskId={taskId} data={data} />
          <FigureLayoutPreview taskId={taskId} data={data} />
        </>
      )}

      <div className="mt-6 flex items-start justify-between">
        <Link
          to={`/drafts/${taskId}/figures/define`}
          className={buttonChrome({ variant: "secondary" })}
        >
          <span aria-hidden="true">←</span> Back
        </Link>
        <div className="flex flex-col items-end gap-1.5">
          <Button
            disabled={!insertAllowed || review.isPending}
            onClick={insert}
          >
            {/* Named from the stage order, so a reordered rail cannot leave it naming another stage. */}
            Continue to {stageAfter("figures")?.label}{" "}
            <span aria-hidden="true">→</span>
          </Button>
          <span className="text-[11px] text-muted-foreground">
            {insertAllowed
              ? "You can still edit the inserted content at any time."
              : "Review and confirm all sections above to enable insertion."}
          </span>
        </div>
      </div>
    </StagePage>
  );
}
